import uniq from "lodash/uniq";

import { AppKind, SerializedComponent } from "../../components/SerializedApp";
import {
    PropertyDescription,
    ArrayContentDescription,
    PropertyKind,
    getPrimitiveNonHiddenColumns,
    ArrayScreenFormat,
    getTableColumn,
    TableGlideType,
    getTableColumnDisplayName,
    MutatingScreenKind,
    InputOutputTables,
    SourceColumnKind,
    getSourceColumnPath,
} from "../description";
import {
    PropertyDescriptor,
    PropertyTableGetter,
    ComponentBuilder,
    ComponentSpecialCaseDescriptor,
    ComponentConfiguratorContext,
    ActionPropertyDescriptor,
} from "../components/index";
import { getColumnProperty, getSourceColumnProperty } from "../components/property-utils";
import { ArrayContentHandlerBase, InlineListBuild } from "./array-content";
import { PropertySection } from "../components/property-sections";
import {
    ColumnPropertyHandler,
    ColumnPropertyFlag,
    isRequiredPropertyHandler,
    EnumPropertyHandler,
} from "../components/property-handlers";
import { AppEnvironment, AppOther, PotentialValue, ValueProducer } from "../../components/types";
import { SerializedElementBuilder } from "../serialized-component";
import {
    makeValidationFlagForProperty,
    setColumnInRowForEditComponent,
    getDefaultValuesForComponent,
} from "../builder-utils";
import { ChoiceStyle } from "../components/choice-types";
import { getPropertyWithColumnFormat } from "../formulas";
import { makeDefaultValuePropertyDescriptor } from "../components/descriptor-utils";
import {
    asArrayValue,
    asBasePrimitiveValue,
    isLoadedDefined,
    loadedDefinedMap,
} from "../../components/component-helpers";
import { BasePrimitiveValue, ValueListener } from "../../components/computation-types";

interface ChoiceArrayContentDescription extends ArrayContentDescription {
    readonly valueProperty: PropertyDescription;
    readonly defaultValue: PropertyDescription | undefined;
    readonly optionProperty: PropertyDescription;
    readonly displayProperty: PropertyDescription;
    readonly isRequired: PropertyDescription;
}

const valuePropertyHandler: ColumnPropertyHandler<ChoiceArrayContentDescription> = new ColumnPropertyHandler(
    "valueProperty",
    "Column",
    [
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.DefaultCaption,
        ColumnPropertyFlag.EditedInApp,
    ],
    undefined,
    undefined,
    getPrimitiveNonHiddenColumns,
    "string",
    PropertySection.Data
);
const stylePropertyHandler = new EnumPropertyHandler(
    { style: ChoiceStyle.Dropdown },
    "Selection Type",
    "",
    [
        {
            value: ChoiceStyle.Dropdown,
            label: "List",
            icon: "choiceDropdown",
        },
        {
            value: ChoiceStyle.Inline,
            label: "Segmented",
            icon: "choiceInline",
        },
    ],
    PropertySection.Design,
    "small-images"
);

class SubscribingForcer implements ValueListener<AppOther, AppEnvironment> {
    private readonly _subscribed = new Set<ValueProducer>();

    public force = (vp: ValueProducer): PotentialValue => {
        if (!this._subscribed.has(vp)) {
            this._subscribed.add(vp);
            vp.subscribe(this);
        }
        return vp.current;
    };

    public retire(): void {
        for (const vp of this._subscribed) {
            vp.unsubscribe(this);
        }
    }

    public setDirty(): void {
        return;
    }

    public followPath(vp: ValueProducer, path: readonly string[]): ValueProducer | undefined {
        for (const n of path) {
            const inspector = vp.getObjectInspector(this.force);
            const p = inspector?.getProperty(n);
            if (!isLoadedDefined(p)) return undefined;
            vp = p;
        }
        return vp;
    }
}

export class ChoiceArrayContentHandler extends ArrayContentHandlerBase<ChoiceArrayContentDescription> {
    constructor() {
        super(ArrayScreenFormat.Choice);
    }

    public get isEditor(): boolean {
        return true;
    }

    public get defaultIsTable(): boolean {
        return true;
    }

    public get specialCaseDescriptor(): ComponentSpecialCaseDescriptor {
        return {
            name: "Choice",
            analyticsName: "choice",
            description: "Choose one out of many options",
            img: "componentChoice",
            group: "Pickers",
            appKinds: AppKind.App,
        };
    }

    public needsCaption(_desc: ChoiceArrayContentDescription | undefined): PropertySection | undefined {
        return PropertySection.Design;
    }

    public getDescriptiveName(
        desc: ChoiceArrayContentDescription,
        caption: string | undefined,
        inlineListHostTable: TableGlideType | undefined
    ): [string, string] | undefined {
        const propertyName = valuePropertyHandler.getColumnName(desc);
        if (propertyName !== undefined && inlineListHostTable !== undefined) {
            const column = getTableColumn(inlineListHostTable, propertyName);
            if (column !== undefined) {
                caption = getTableColumnDisplayName(column);
            }
        }

        if (caption === undefined) return undefined;
        return ["Choice", caption];
    }

    public get needsOutputContext(): boolean {
        return true;
    }

    public get sourcePropertyLabel(): readonly [string, PropertySection] {
        return ["Sheet", PropertySection.Source];
    }

    public makeContentForProcessedArray(
        builder: ComponentBuilder,
        desc: ChoiceArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        _searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        _uniqueID: string,
        caption: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        if (inlineListBuild === undefined) return b.emptyUI;
        const { tables, mutatingScreenKind, outputContext } = inlineListBuild;
        if (outputContext === undefined) return b.emptyUI;

        const propertyName = valuePropertyHandler.getColumnName(desc);
        const optionColumnName = getColumnProperty(desc.optionProperty);
        const displayColumnName = getColumnProperty(desc.displayProperty);

        if (propertyName === undefined || optionColumnName === undefined) return b.emptyUI;

        const value = b.getProperty(propertyName, outputContext, table);

        const nameForChoice = getPropertyWithColumnFormat(builder, table, displayColumnName ?? optionColumnName);

        const getOptionProperty = b.getProperty(optionColumnName, b.getContext, table);
        const choices = b.map(
            b.uniqueValues(b.filter(arrayData, b.isDefined(getOptionProperty)), getOptionProperty),
            b.object({
                name: nameForChoice,
                value: getOptionProperty,
            })
        );
        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            value,
            undefined,
            tables.output,
            undefined,
            outputContext
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const style = b.constant(stylePropertyHandler.getEnum(desc));

        const isRequired = isRequiredPropertyHandler.getSwitch(desc);
        return b.let(
            lets,
            b.ui("Choice", {
                value: valueToModify,
                choices,
                caption,
                onChange: action,
                isEnabled,
                isRequired: b.constant(isRequired),
                style,
                pushScreen: builder.makePushChoiceScreen(
                    valueToModify,
                    choices,
                    caption,
                    b.constant(isRequired),
                    action
                ),
            })
        );
    }

    public getContentPropertyDescriptors<T extends ChoiceArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean,
        arrayVP: ValueProducer | undefined
    ): readonly PropertyDescriptor[] {
        const options: BasePrimitiveValue[] = [];

        if (arrayVP !== undefined) {
            const forcer = new SubscribingForcer();
            try {
                const array = loadedDefinedMap(forcer.force(arrayVP), asArrayValue);

                const optionSourceColumn = getSourceColumnProperty(desc?.optionProperty);
                if (array !== undefined && optionSourceColumn?.kind === SourceColumnKind.DefaultContext) {
                    for (const vp of array) {
                        const o = forcer.followPath(vp, getSourceColumnPath(optionSourceColumn));
                        if (o === undefined) continue;

                        const current = forcer.force(o);
                        if (isLoadedDefined(current)) {
                            options.push(asBasePrimitiveValue(current));
                        }
                    }
                }
            } finally {
                forcer.retire();
            }
        }

        const properties: PropertyDescriptor[] = [valuePropertyHandler];

        const defaultProperty = makeDefaultValuePropertyDescriptor(
            ccc,
            mutatingScreenKind,
            "string",
            undefined,
            false,
            uniq(options)
        );
        if (defaultProperty !== undefined) {
            properties.push(defaultProperty);
        }

        properties.push(
            isRequiredPropertyHandler,
            stylePropertyHandler,
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms,
                arrayVP
            ),
            {
                kind: PropertyKind.Column,
                property: { name: "optionProperty" },
                section: PropertySection.Source,
                label: "Values",
                required: true,
                editable: true,
                searchable: false,
                getIndirectTable: getPropertyTable,
                getAllowedColumns: getPrimitiveNonHiddenColumns,
                preferredNames: ["option", "options", "choice", "choices"],
                preferredType: "string",
            },
            {
                kind: PropertyKind.Column,
                property: { name: "displayProperty" },
                section: PropertySection.Source,
                label: "Display as",
                required: false,
                editable: true,
                searchable: false,
                emptyByDefault: true,
                getIndirectTable: getPropertyTable,
                getAllowedColumns: getPrimitiveNonHiddenColumns,
                preferredNames: ["option", "options", "choice", "choices", "display"],
                preferredType: "string",
            }
        );
        return properties;
    }

    public getDefaultValues(
        builder: ComponentBuilder,
        desc: ChoiceArrayContentDescription,
        { input }: InputOutputTables
    ): readonly [string, SerializedComponent][] {
        return getDefaultValuesForComponent(builder, [desc.valueProperty, desc.defaultValue], input, "string");
    }

    public getBasicSearchProperties(): readonly string[] {
        return [];
    }

    public getActionDescriptors<T extends ChoiceArrayContentDescription>(): readonly ActionPropertyDescriptor<T>[] {
        return [];
    }

    public needValidation(desc: ChoiceArrayContentDescription): boolean {
        return isRequiredPropertyHandler.getSwitch(desc);
    }

    public makeValidationFlag(
        b: SerializedElementBuilder,
        desc: ChoiceArrayContentDescription,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined {
        return makeValidationFlagForProperty(b, outputContext, valuePropertyHandler.getColumnName(desc), table);
    }
}
